// The play write-contract.
//
// This module is the single source of truth for how the web client writes
// to Firestore. It mirrors two iOS methods byte-for-byte so the existing
// server pipeline treats web-born users identically to iOS users:
//
//   • ensureUserDoc        ← FirestoreService.createUserProfileIfNeeded
//   • writeAnswer          ← FirestoreService.submitAnswers (per-question)
//
// CRITICAL invariant (verified against functions/src/index.ts:1037):
// `awardSubmissionPoints` does `if (!userDoc.exists) return;` and then an
// `.update()` (not set-merge) on the user doc. So the users/{uid} doc MUST
// exist BEFORE any vote write lands, or the award silently no-ops and the
// player earns nothing. Therefore callers MUST `await ensureUserDoc(...)`
// before the first `writeAnswer(...)`. The Play/Question screens enforce
// this ordering.
//
// Why a user doc isn't created on session start: creating it fires
// `onUserProfileCreated` → founder-group seeding. We defer that to the
// first actual answer so pure bouncers (anon session, never plays) don't
// seed throwaway founder groups. The orphan sweep (P3) cleans up the rest.

import { db, FieldValue, Timestamp } from '../firebase.js';

const DISPLAY_NAME_CAP = 32;

/**
 * Drain `?ref=<sharerUid>&card=<cardType>` from the current URL into the
 * iOS-shaped `attribution` map, so a web-origin signup credits its sharer
 * when the account is later secured / the app is downloaded (plan §7.7).
 * Self-attribution (ref === own uid) is filtered. Returns null if absent.
 */
const REF_KEY = 'ps_ref';
const REF_CARD_KEY = 'ps_ref_card';
const UTM_KEY = 'ps_utm';
// The four standard campaign params we persist. Stored on the user doc as
// `attribution.utm.{source,medium,campaign,content}` so a PAID-ads cohort (e.g. a
// promoted X post tagged utm_medium=paid) is isolable from organic in the admin
// Growth dashboard. See docs/paid-pilot-x-runbook.md for the tagging scheme.
const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];

/** Read the campaign UTMs from a URLSearchParams into a compact {source,…} map, or null. */
function utmFromParams(params) {
  const utm = {};
  for (const k of UTM_FIELDS) {
    const v = params.get(k);
    if (v) utm[k.slice(4)] = v.slice(0, 120); // 'utm_source' → 'source', length-capped
  }
  return Object.keys(utm).length > 0 ? utm : null;
}

/**
 * Stash `?ref`/`?card` into sessionStorage at FIRST app load. The user doc is
 * created lazily (first vote / save), by which point client navigation has
 * usually stripped the params from the URL — so without this, a referred
 * friend who clicks around before their doc is created loses the attribution.
 * Call once on session start. No-op if already captured or no ref present.
 */
export function captureRefParam() {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref && ref.length > 0 && !sessionStorage.getItem(REF_KEY)) {
      sessionStorage.setItem(REF_KEY, ref);
      const card = params.get('card');
      if (card) sessionStorage.setItem(REF_CARD_KEY, card);
    }
    // Stash campaign UTMs at first load too: the /go redirect + client-side
    // navigation strip the query string before the user doc is created lazily,
    // so a paid visitor who clicks around before voting would otherwise lose them.
    if (!sessionStorage.getItem(UTM_KEY)) {
      const utm = utmFromParams(params);
      if (utm) sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
    }
  } catch {
    /* no-op: storage unavailable / malformed URL */
  }
}

export function readAttributionFromUrl(uid) {
  let ref = null;
  let card = 'q_page';
  let utm = null;
  try {
    const params = new URLSearchParams(window.location.search);
    ref = params.get('ref');
    card = params.get('card') || 'q_page';
    utm = utmFromParams(params);
    // Fall back to the values stashed at first load (see captureRefParam) so
    // attribution survives client-side navigation / reloads within the session.
    if (!ref) {
      try {
        ref = sessionStorage.getItem(REF_KEY);
        card = sessionStorage.getItem(REF_CARD_KEY) || card;
      } catch {
        /* storage unavailable */
      }
    }
    if (!utm) {
      try {
        const stashed = sessionStorage.getItem(UTM_KEY);
        if (stashed) utm = JSON.parse(stashed);
      } catch {
        /* storage unavailable / malformed JSON */
      }
    }
  } catch {
    /* no-op: malformed URL */
  }
  const isReferral = !!ref && ref.length > 0 && ref !== uid;
  // Organic / direct with no ref AND no campaign tag → null, exactly as before
  // (keeps the user doc clean — no empty attribution map for pure-organic users).
  if (!isReferral && !utm) return null;
  const attr = {
    source: 'url_param',
    capturedAt: FieldValue.serverTimestamp(),
  };
  if (isReferral) {
    attr.sharerUid = ref;
    attr.cardType = card;
  }
  // {source, medium, campaign, content} — the paid-cohort key. A promoted X post
  // tagged utm_medium=paid lands here even without a ?ref (paid clicks aren't referrals).
  if (utm) attr.utm = utm;
  return attr;
}

/**
 * Build the new-user document. Mirrors the field set written by iOS
 * `createUserProfileIfNeeded` (FirestoreService.swift:682) so the server
 * pipeline + any future iOS hydration see a familiar doc. `users/{uid}`
 * create is unrestricted for the owner in firestore.rules, so every field
 * here is permitted at create time.
 */
function buildNewUserDocFields(displayName, attribution) {
  const safeName =
    (displayName && displayName.trim().slice(0, DISPLAY_NAME_CAP)) || 'Guest';
  const browserTz =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';

  const data = {
    displayName: safeName,
    currentStreak: 0,
    longestStreak: 0,
    overallAccuracy: 0,
    rollingAccuracy: 0,
    questionsAnswered: 0,
    totalPoints: 0,
    strongestCategory: 'Markets',
    strongestCategoryAccuracy: 0,
    weakestCategory: '',
    weakestCategoryAccuracy: 0,
    isAppleIDLinked: false,
    categoryAccuracies: {},
    communityAccuracy: 0,
    timezone: browserTz,
    notificationsEnabled: true,
    notifyMorningReminder: true,
    notifyFriendlyNudge: true,
    notifyResultsReady: true,
    notifyMessageMentions: true,
    nudgeHour: 13,
    nudgeMinute: 0,
    reminderHour: 7,
    reminderMinute: 0,
    resultsHour: 18,
    resultsMinute: 0,
    lastSubmissionPointsDate: '',
    submissionPointsTotal: 0,
    isFoundingMember: false,
    createdAt: FieldValue.serverTimestamp(),
    nextOnboardingMilestone: 'first_submission',
    hasShared: false,
    totalDaysAnswered: 0,
    acceptsChallenges: true,
    publicLeaderboardOptIn: true,
    hasSeenPublicLeaderboardNudge: false,
    // Origin marker — benign, read by nothing in P1, useful for the P3
    // orphan sweep + analytics to identify web-born accounts.
    platform: 'web',
  };
  if (attribution) data.attribution = attribution;
  return data;
}

/**
 * Ensure the users/{uid} doc exists. Idempotent: a no-op if already there.
 * Creating it fires the founder-group seed trigger (intended). Pass the
 * user's display name (defaults to "Guest" for anonymous play).
 *
 * @returns {Promise<void>}
 */
export async function ensureUserDoc(uid, displayName) {
  const ref = db.collection('users').doc(uid);
  const snap = await ref.get();
  if (snap.exists) return;
  const attribution = readAttributionFromUrl(uid);
  await ref.set(buildNewUserDocFields(displayName, attribution));
}

/**
 * Doc-write shape for one question's answer + vote, mirroring iOS
 * submitAnswers exactly. Each caller below writes both docs in a batch:
 *   • users/{uid}/answers/{questionId}   (the user's record)
 *   • questions/{questionId}/votes/{uid} (triggers awardSubmissionPoints)
 *
 * The vote write is what the server reacts to; the all-six gate awards
 * +6 once the 6th vote of today's set exists. Incremental (one tap at a
 * time) is fully supported by the gate.
 *
 * `isDoubleDown` (web double-down shipped 2026-07-08 — reverses the prior
 * 2026-06-13 "app-only" call): only `submitDailySet` passes it, since the
 * 2× pick is a today-ballot concept — the single-question `writeAnswer`
 * door (`/q/<id>`) never sets it. `true` → stamp `isDoubleDown:true` on
 * both docs; `false`/`undefined` → omit the field (equivalent to falsy for
 * `resolveQuestion`'s `voteData.isDoubleDown === true` scoring check —
 * these are fresh docs on initial submission, so there's no prior value to
 * clear via `FieldValue.delete()`; that's only needed by the post-submit
 * `changeDoubleDown` callable, which is server-side).
 *
 * @param {string} uid
 * @param {object} question  must carry { id, text, category, publishDate:Date,
 *                            estimatedResolutionDate:Date, isResolved,
 *                            communityYesPercent, communityNoPercent }
 * @param {{answer?: boolean, isSkipped?: boolean}} choice
 */

/** The users/{uid}/answers/{qid} payload for one question. */
function answerDocData(question, answer, isSkipped, isDoubleDown) {
  const data = {
    questionId: question.id,
    questionText: question.text ?? '',
    category: question.category ?? 'Markets',
    publishDate: Timestamp.fromDate(toDate(question.publishDate)),
    estimatedResolutionDate: Timestamp.fromDate(
      toDate(question.estimatedResolutionDate)
    ),
    isSkipped,
    isResolved: question.isResolved === true,
    communityYesPercent: numOr(question.communityYesPercent, 50),
    communityNoPercent: numOr(question.communityNoPercent, 50),
    submittedAt: FieldValue.serverTimestamp(),
    answeredAt: FieldValue.serverTimestamp(),
  };
  if (typeof answer === 'boolean') data.answer = answer;
  if (isDoubleDown === true) data.isDoubleDown = true;
  return data;
}

/** The questions/{qid}/votes/{uid} payload (triggers awardSubmissionPoints). */
function voteDocData(uid, answer, isSkipped, isDoubleDown) {
  const data = {
    userId: uid,
    isSkipped,
    submittedAt: FieldValue.serverTimestamp(),
  };
  if (typeof answer === 'boolean') data.answer = answer;
  if (isDoubleDown === true) data.isDoubleDown = true;
  return data;
}

/** Write ONE question's answer + vote in a batch (used by the /q single-question door). */
export async function writeAnswer(uid, question, choice) {
  const { answer, isSkipped = false } = choice;
  const batch = db.batch();
  batch.set(
    db.collection('users').doc(uid).collection('answers').doc(question.id),
    answerDocData(question, answer, isSkipped),
    { merge: true }
  );
  batch.set(
    db.collection('questions').doc(question.id).collection('votes').doc(uid),
    voteDocData(uid, answer, isSkipped),
    { merge: true }
  );
  await batch.commit();
}

/**
 * Submit the full daily set in a SINGLE batch — mirrors iOS submitAnswers.
 * Writes all answered/skipped questions' answer + vote docs at once. The
 * server's all-six gate (awardSubmissionPoints) awards +6 when the 6th vote
 * lands. Used by /play's collect-then-submit flow; callers MUST await
 * ensureUserDoc first so the award can fire.
 *
 * @param {string} uid
 * @param {Array<object>} questions  each with { id, text, ..., answer?:boolean,
 *   isSkipped?:boolean, isDoubleDown?:boolean } — `isDoubleDown` is set by the
 *   ballot's 2× toggle (Play.jsx) on at most one question; stamped onto that
 *   question's docs only.
 */
export async function submitDailySet(uid, questions) {
  const batch = db.batch();
  for (const q of questions) {
    // A question that resolved/voided mid-day is already scored server-side —
    // exclude it from the batch so it can't block or corrupt the submit (its
    // card renders no Yes/No anyway) (audit B2).
    if (q.isResolved === true) continue;
    const answered = typeof q.answer === 'boolean';
    if (!answered && !q.isSkipped) continue; // unanswered → don't write
    const isDoubleDown = q.isDoubleDown === true && !q.isSkipped;
    batch.set(
      db.collection('users').doc(uid).collection('answers').doc(q.id),
      answerDocData(q, q.answer, q.isSkipped === true, isDoubleDown),
      { merge: true }
    );
    batch.set(
      db.collection('questions').doc(q.id).collection('votes').doc(uid),
      voteDocData(uid, q.answer, q.isSkipped === true, isDoubleDown),
      { merge: true }
    );
  }
  await batch.commit();
}

/** Coerce a possibly-Date / possibly-Timestamp / possibly-missing into a Date. */
function toDate(v) {
  if (v instanceof Date) return v;
  if (v && typeof v.toDate === 'function') return v.toDate();
  return new Date();
}

function numOr(v, fallback) {
  return typeof v === 'number' && !isNaN(v) ? v : fallback;
}
